@use '../../style/functions';
@use '../../style/mixins';

/**
 * @prop --circular-progress-size: Determines the visual size of the visualization. This does not override the `size` property if it is specified.
 * @prop --circular-progress-track-color: Determines the color of the circular track. Defaults to `--contrast-400`.
 * @prop --circular-progress-suffix-color: Determines the color of the prefix. Defaults to `--contrast-1000`.
 * @prop --circular-progress-text-color: Determines the color of the value. Defaults to `--contrast-1200`.
 * @prop --circular-progress-prefix-color: Determines the color of the suffix. Defaults to `--contrast-1000`.
 * @prop --circular-progress-fill-color: Determines the color of the progressed section. Defaults to `--lime-primary-color`.
 * @prop --circular-progress-background-color: Determines the background color of the central section. Defaults to `--contrast-100`.
 */

:host {
    display: block;
    box-sizing: border-box;
    isolation: isolate;
}

:host([size='x-small']) {
    --circular-progress-size: 1.5rem;
    font-weight: bold;

    .value {
        letter-spacing: functions.pxToRem(-1);
    }
}

:host([size='small']) {
    --circular-progress-size: 2rem;
    font-weight: bold;

    .value {
        letter-spacing: functions.pxToRem(-0.5);
    }
}

:host([size='medium']) {
    --circular-progress-size: 3rem;
}

:host([size='large']) {
    --circular-progress-size: 4rem;
}

:host([size='x-large']) {
    --circular-progress-size: 5rem;
}

.lime-circular-progress {
    --size: var(--circular-progress-size, 3rem);
    --fill-color: var(
        --circular-progress-fill-color,
        var(--lime-primary-color, var(--limel-theme-primary-color))
    );
    --track-color: var(
        --circular-progress-track-color,
        rgb(var(--contrast-400))
    );
    position: relative;

    display: flex;
    align-items: center;
    justify-content: center;

    width: var(--size);
    height: var(--size);
    border-radius: 50%;

    line-height: normal;

    box-shadow: 0 0 0 0.125rem rgb(var(--contrast-100), 0.7);
    background: conic-gradient(
        var(--fill-color) 0% var(--percentage),
        var(--track-color) var(--percentage) 100%
    );

    &:before {
        $size-of-middle-circle: calc(
            var(--size) * 0.75 + 0.25rem
        ); // this calculation ensures that the middle circle is responsive and always perfectly centered

        content: '';
        position: absolute;
        width: $size-of-middle-circle;
        height: $size-of-middle-circle;
        border-radius: 50%;
        background-color: var(
            --circular-progress-background-color,
            rgb(var(--contrast-100))
        );
        box-shadow: var(--button-shadow-pressed);
    }
}

.prefix {
    font-size: clamp(0.5rem, calc(var(--size) * 0.16), 2.25rem);
    @include mixins.truncate-text();
    text-align: center;
    color: var(--circular-progress-prefix-color, rgb(var(--contrast-1000)));

    width: 45%;
    top: 20%;
    position: absolute;
}

.value {
    display: flex;
    font-size: clamp(0.5rem, calc(var(--size) * 0.25), 4rem);
    color: var(--circular-progress-text-color, rgb(var(--contrast-1200)));
    z-index: 1;
    cursor: default;
}

.suffix {
    font-size: clamp(0.5rem, calc(var(--size) * 0.18), 2.5rem);
    color: var(--circular-progress-suffix-color, rgb(var(--contrast-1000)));
    padding-top: 4%;
}

@import './partial-styles/_percentage-colors';
